<script lang="ts">
    import * as Accordion from "./ui/accordion/index.js";
    import * as Tabs from "./ui/tabs/index.js";
    import * as Card from "./ui/card/index.js";
    import ErrorMessage from "./common/error-message.svelte";
    import Date from "./common/date.svelte";
    import MailboxAttachment from "./mailbox-attachment.svelte";
    import { m } from "../messages.svelte.js";
    import { getClient } from "../utils.js";
    import type { ApprovalTaskId, AttachmentId, InboxId, Thread, ThreadId } from "@repo/backend/types";
    import Spinner from "./salesorder/spinner.svelte";
    import LoadingMessage from "./common/loading-message.svelte";

    const {
        onThread,
        messageIds,
        attachmentIds,
        deferRenderUntilMessageIds,
        ...props
    }: {
        onThread?: (x: Thread) => void;
        messageIds?: string[];
        attachmentIds?: string[];
        deferRenderUntilMessageIds?: boolean;
    } & (
        | { threadId: ThreadId }
        | { externalThreadId: string; inboxId?: InboxId }
        | { approvalTaskId: ApprovalTaskId; approvalTaskToken: string }
    ) = $props();
    const promise = $derived.by(async () => {
        function handleResult(t: Thread) {
            onThread?.(t);
            return t;
        }

        if ("approvalTaskId" in props) {
            return handleResult(
                await getClient()["Get thread for approval task"]({
                    params: { approvalTaskId: props.approvalTaskId },
                    query: { token: props.approvalTaskToken },
                }),
            );
        }

        return handleResult(
            await getClient()["Get thread by any id"]({
                jsonBody: {
                    id: "externalThreadId" in props ? props.externalThreadId : props.threadId,
                },
            }),
        );
    });

    $effect(() => {
        promise.then(x => {
            onThread?.(x);
        });
    });

    const renderSpinner = $derived(deferRenderUntilMessageIds && !messageIds && !attachmentIds);
</script>

{#await promise}
    {#if renderSpinner}
        <LoadingMessage />
    {/if}
{:then t}
    {#if renderSpinner}
        <LoadingMessage />
    {:else}
        <!-- If messageIds is empty, we show all messages, to be safe -->
        {@const filteredMessages = messageIds?.length ? t.messages.filter(m => messageIds.includes(m.id)) : t.messages}
        <!-- ...not for attachments though -->
        {@const filteredAttachments = attachmentIds
            ? Object.entries(t.attachments).filter(([aId]) => attachmentIds.includes(aId))
            : Object.entries(t.attachments)}
        <Tabs.Root value="messages" class="flex h-full flex-col gap-2">
            <Tabs.List class="mx-2 mt-2">
                <Tabs.Trigger value="messages">{m["assistant.messages"]()}</Tabs.Trigger>
                <Tabs.Trigger value="attachments" disabled={filteredAttachments.length === 0}>
                    {m["assistant.attachments"]()}
                    {`(${filteredAttachments.length})`}
                </Tabs.Trigger>
            </Tabs.List>
            <Tabs.Content value="messages" class="flex h-full flex-col gap-2 overflow-auto px-2 pb-2">
                {#if t}
                    {#each filteredMessages as m (m.id)}
                        {@const isHtml = /<(?:html|head|body|div|p|table|span|a|img|li)\b/i.test(m.content)}
                        <Card.Root>
                            <Card.Header>
                                <div class="flex items-center justify-between gap-2">
                                    <Card.Title class="min-w-0 truncate">{m.subject}</Card.Title>
                                    <span class="text-muted-foreground shrink-0 text-xs">
                                        <Date value={m.receivedAt} withTime />
                                    </span>
                                </div>
                                <div class="text-muted-foreground flex flex-col gap-x-4 gap-y-1 text-xs">
                                    <div>From: {m.senderName} &lt;{m.senderEmail}&gt;</div>
                                    {#if m.to.length > 0}
                                        <div>To: {m.to.join(", ")}</div>
                                    {/if}
                                    {#if m.cc.length > 0}
                                        <div>Cc: {m.cc.join(", ")}</div>
                                    {/if}
                                    {#if m.bcc.length > 0}
                                        <div>Bcc: {m.bcc.join(", ")}</div>
                                    {/if}
                                    {#if m.sent}
                                        <div class="text-blue-600 dark:text-blue-400">Sent</div>
                                    {/if}
                                </div>
                            </Card.Header>
                            <Card.Content>
                                <iframe
                                    {@attach el => {
                                        function fn() {
                                            const ro = new ResizeObserver(() => {
                                                if (el.contentDocument?.documentElement) {
                                                    el.height = String(
                                                        el.contentDocument?.documentElement.offsetHeight + 1,
                                                    );
                                                }
                                            });
                                            if (el.contentDocument?.documentElement) {
                                                ro.observe(el.contentDocument?.documentElement);
                                            }
                                            el.contentDocument?.body.querySelectorAll("a").forEach(x => {
                                                x.addEventListener("click", ev => {
                                                    ev.preventDefault();
                                                    alert(x.href);
                                                });
                                            });
                                            el.contentDocument?.body.querySelectorAll("img").forEach(async img => {
                                                const matches = /cid:([^@]+)/.exec(img.src);
                                                if (matches && matches[1]) {
                                                    const a = filteredAttachments.find(
                                                        y => y[1].filename === matches[1],
                                                    );
                                                    if (a) {
                                                        img.src =
                                                            "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCI+PHN0eWxlPkBrZXlmcmFtZXMgc3Bpbm5lcl9zdnYye3Rve3RyYW5zZm9ybTpyb3RhdGUoMzYwZGVnKX19PC9zdHlsZT48cGF0aCBkPSJNMTAuMTQgMS4xNmExMSAxMSAwIDAgMC05IDguOTJBMS41OSAxLjU5IDAgMCAwIDIuNDYgMTJhMS41MiAxLjUyIDAgMCAwIDEuNjUtMS4zIDggOCAwIDAgMSA2LjY2LTYuNjFBMS40MiAxLjQyIDAgMCAwIDEyIDIuNjlhMS41NyAxLjU3IDAgMCAwLTEuODYtMS41M1oiIHN0eWxlPSJhbmltYXRpb246c3Bpbm5lcl9zdnYyIC43NXMgaW5maW5pdGUgbGluZWFyIiB0cmFuc2Zvcm0tb3JpZ2luPSJjZW50ZXIiLz48L3N2Zz4=";
                                                        img.style.objectFit = "none";
                                                        const data =
                                                            a[1].data ??
                                                            (await getClient()
                                                                ["Select attachments base64 content"]({
                                                                    jsonBody: [
                                                                        {
                                                                            inboxId: t.inboxId,
                                                                            attachmentId: a[0] as AttachmentId,
                                                                        },
                                                                    ],
                                                                })
                                                                .then(r => r[0]?.data));
                                                        if (data) {
                                                            img.src = `data:${a[1].contentType};base64,${data}`;
                                                            img.style.objectFit = "";
                                                        }
                                                    }
                                                }
                                            });
                                        }
                                        el.addEventListener("load", fn);
                                        return () => el.removeEventListener("load", fn);
                                    }}
                                    title={m.id}
                                    srcdoc={isHtml
                                        ? m.content
                                        : `<body style="white-space:pre-wrap">${m.content}</body>`}
                                    width="100%"
                                    height="100%"
                                    style="border: none; overflow: hidden;"
                                ></iframe>
                            </Card.Content>
                        </Card.Root>
                    {/each}
                {:else}
                    <Spinner />
                {/if}
            </Tabs.Content>
            <Tabs.Content value="attachments" class="flex h-full flex-col gap-2 overflow-auto px-2 pb-2">
                {#if t}
                    {@const sorted = filteredAttachments.sort(a =>
                        a[1].contentType.toLowerCase().includes("pdf") || a[1].filename.toLowerCase().includes("pdf")
                            ? -1
                            : 0,
                    )}
                    {@const expandId = sorted.find(
                        ([, a]) =>
                            a.contentType.toLowerCase().includes("pdf") || a.filename.toLowerCase().includes("pdf"),
                    )?.[0] ?? sorted[0]?.[0]}
                    <Accordion.Root type="multiple" value={expandId ? [expandId] : undefined}>
                        {#each sorted as [id, a] (id)}
                            <MailboxAttachment
                                attachmentId={id as never}
                                contentType={a.contentType}
                                filename={a.filename}
                                inboxId={t.inboxId}
                                data={a.data}
                            />
                        {/each}
                    </Accordion.Root>
                {:else}
                    <Spinner />
                {/if}
            </Tabs.Content>
        </Tabs.Root>
    {/if}
{:catch e}
    <ErrorMessage {e} />
{/await}
